import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, CircularProgress, Divider, Paper, Stack } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';

import BuildingBackBooksAccountRow from './BuildingBackBooksAccountRow';
import toRows, {
    BackBooksRow,
    canFileUploads,
    hasResolvableAccount,
    isBulkBankStatementUploadContext,
    isRelevantToAsk,
    statusBelongsToAccount,
} from './toRows';

import { sendDatadogRumAction, sendDatadogRumError } from 'utils/analytics/datadogRum';
import TaskGroupDrawer from 'modules/Tasks/Groups/TaskGroupDrawer';
import UnusedAccountsHint from 'modules/Tasks/Groups/customComponents/UnusedAccountsHint';
import { ACTIVE_TASK_STATUSES, GROUP_PANEL_TASK_STATUSES, TaskGroupSummary, TaskStatus } from 'modules/Tasks/types';
import { TaskStatus as OperatorTaskStatus } from 'modules/common/components/MemberOperator/MemberOperator.interfaces';
import { StatementFile } from 'modules/common/components/VerifiedStatementUpload/components/StatementSlotList';
import { groupHeldPeriods } from 'modules/common/components/VerifiedStatementUpload/utils/periods';
import { STATEMENT_DOCUMENT_TYPE } from 'modules/common/components/VerifiedStatementUpload/components/StatementUploadArea';
import VerifiedStatementUploadModal from 'modules/common/components/VerifiedStatementUpload/VerifiedStatementUploadModal';
import useStatementDocumentWrites, {
    ERROR_SNACKBAR_DURATION_MS,
} from 'modules/common/components/VerifiedStatementUpload/hooks/useStatementDocumentWrites';
import { Document } from 'modules/common/interfaces/documents';
import { useDocumentFiltered } from 'modules/common/queries/documents';
import { useMember } from 'modules/common/queries/member';
import useMemberTasks from 'services/memberTasks/queries/useMemberTasks';
import useStatementCoverage, {
    COVERAGE_UNAVAILABLE_MESSAGE,
    isCoverageUsable,
    STATEMENT_COVERAGE_QUERY_KEY,
    StatementPeriod,
} from 'services/bookkeeping/queries/useStatementCoverage';
import { StatementPeriodAssignment } from 'services/bookkeeping/mutations/useReassignStatementPeriods';
import useStatementUploadStatuses, {
    STATEMENT_UPLOAD_STATUSES_QUERY_KEY,
} from 'services/bookkeeping/queries/useStatementUploadStatuses';
import useUpdateOperatorTaskStatus from 'services/operatorTasks/mutations/useUpdateOperatorTaskStatus';
import useInvalidateMemberTaskQueries from 'services/memberTasks/useInvalidateMemberTaskQueries';

/**
 * Shown instead of opening the modal for an account with no Tight id.
 *
 * Not "try again": nothing the member does resolves it — the account has to be assigned an id
 * upstream first, so the honest message is that we are not ready for their file yet.
 */
const ACCOUNT_NOT_READY_MESSAGE = "We're still setting up this account. We'll ask for its statements shortly.";

/**
 * Shown when the upload landed but filing it against this account did not.
 *
 * Its own sentence rather than onboarding's `UPLOAD_SAVE_FAILED_MESSAGE`, which describes a failed
 * write to the onboarding step record — a different failure that happens to be recoverable the same
 * way. One string covering both meant editing the copy for either surface silently reworded the
 * other. Says "attach" because that is the step that failed: the file exists and is listed, it is
 * the link to this account that is missing.
 */
const UPLOAD_NOT_FILED_MESSAGE =
    "Your file was uploaded, but we couldn't attach it to this account. Please try uploading it again.";

interface GroupBodyProps {
    readonly rows: BackBooksRow[];
    readonly isLoading: boolean;
    readonly isError: boolean;
    /** Coverage answered, but cannot report accounts — distinct from "no accounts owe anything". */
    readonly isCoverageUnusable: boolean;
    readonly onUploadClick: (rowKey: string) => void;
}

/**
 * Loading, error and empty states deliberately match `GroupTaskList`, the default group body — a
 * custom panel that reports failure differently reads as a different kind of failure.
 */
function GroupBody({ rows, isLoading, isError, isCoverageUnusable, onUploadClick }: Readonly<GroupBodyProps>) {
    if (isLoading) {
        return (
            <Stack sx={{ alignItems: 'center', py: 4 }}>
                <CircularProgress />
            </Stack>
        );
    }

    if (isError) {
        return <Alert severity="error">We couldn&apos;t load these tasks. Please try again.</Alert>;
    }

    if (isCoverageUnusable) {
        return <Alert severity="warning">{COVERAGE_UNAVAILABLE_MESSAGE}</Alert>;
    }

    if (rows.length === 0) {
        return null;
    }

    return (
        <Stack sx={{ gap: 2 }}>
            <Paper variant="outlined">
                <Stack divider={<Divider />}>
                    {rows.map((row) => (
                        <BuildingBackBooksAccountRow
                            key={row.task.id}
                            rowKey={row.task.id}
                            account={row.account}
                            state={row.state}
                            askedPeriods={row.askedPeriods}
                            missingCount={row.missing.length}
                            onUploadClick={onUploadClick}
                        />
                    ))}
                </Stack>
            </Paper>
            <UnusedAccountsHint />
        </Stack>
    );
}

interface BuildingBackBooksGroupProps {
    readonly open: boolean;
    readonly onClose: () => void;
    readonly group: TaskGroupSummary;
}

/**
 * Custom group flow for the `building_back_books` task group: the accounting-setup statement upload
 * relocated to the dashboard, one row per bank account that owes past statements.
 *
 * Rows come from the child tasks; every number comes from live coverage. See `toRows` for why.
 *
 * Unlike the onboarding surface this stores nothing of its own — there is no step blob here, so no
 * write queue and no staleness refs. Coverage is the record.
 */
export default function BuildingBackBooksGroup({ open, onClose, group }: Readonly<BuildingBackBooksGroupProps>) {
    const queryClient = useQueryClient();
    const { enqueueSnackbar } = useSnackbar();
    const { data: member } = useMember();
    const memberId = member?.id;

    const [activeRowKey, setActiveRowKey] = useState<string | null>(null);
    /**
     * What this session dropped on which row, and whether the server accepted the filing.
     *
     * Both facts are needed and one flag cannot carry them. `rowKey` keeps a document visible on the
     * row it was dropped on even when the server reports it filed against nothing — otherwise a
     * failed `assign-account` makes the member's file disappear with no way to see or retry it.
     * `isFiled` is the dedupe: the dropzone re-reports its whole accumulated batch on every upload,
     * and only a document already accepted should be skipped, so a failure stays retryable.
     */
    const uploadsThisSessionRef = useRef<Map<string, { rowKey: string; isFiled: boolean }>>(new Map());

    // Only fetch once the panel is open — keeps closed group cards from firing a query each.
    const {
        data: coverage,
        isPending: isCoveragePending,
        isError: isCoverageError,
    } = useStatementCoverage({ enabled: open });
    const {
        data: tasks,
        isLoading: isTasksLoading,
        isError: isTasksError,
    } = useMemberTasks({ groupId: group.id, statuses: GROUP_PANEL_TASK_STATUSES }, { enabled: open });
    const {
        data: documents,
        isError: isDocumentsError,
        isPending: isDocumentsPending,
    } = useDocumentFiltered({ document_type: STATEMENT_DOCUMENT_TYPE }, { enabled: open });
    /**
     * Loaded with the drawer rather than with the modal, even though only the modal's file list
     * reads it. Gated on the open row it answered `undefined` for the whole of the first request,
     * and an undefined map is indistinguishable from "nothing uploaded" — a member with statements
     * already filed opened the modal onto an empty list and re-uploaded everything.
     *
     * Not an extra request: the modal's verdict hook shares this key, so this only moves the fetch
     * earlier. Cheap by design — a five-second `staleTime` and no Tight round trip.
     */
    const {
        data: statuses,
        isError: isStatusesError,
        isPending: isStatusesPending,
    } = useStatementUploadStatuses({ enabled: open });

    const { savePeriods, removeFromAccount, holdsMonthAfterRemoval, fileDocuments } = useStatementDocumentWrites();

    const rows = useMemo(() => toRows(tasks ?? [], coverage?.accounts ?? []), [tasks, coverage]);

    /**
     * Report a task that names no account, which is now the only way the join can lose one.
     *
     * The producer never writes a null `bank_account_id`, so this firing means that guarantee has
     * broken — and nothing else would surface it: the member simply sees fewer rows than the group
     * card counts. Dropping is the safe branch (see `toRows` for why there is no fallback), and it
     * is silent, which is exactly why it needs reporting.
     *
     * An action rather than an error: it needs a frequency, and a stack would only point back here.
     */
    const unresolvableTaskIds = useMemo(
        () =>
            (tasks ?? [])
                .filter((task) => isBulkBankStatementUploadContext(task.context) && !hasResolvableAccount(task.context))
                .map((task) => task.id),
        [tasks]
    );

    // Joined into the dependency so a refetch that changes nothing relevant does not re-report.
    const unresolvableSignature = unresolvableTaskIds.join(',');
    useEffect(() => {
        if (unresolvableSignature === '') {
            return;
        }
        sendDatadogRumAction('Back-books task carries no bank account id', {
            source: 'BuildingBackBooksGroup',
            taskIds: unresolvableSignature,
            droppedCount: unresolvableSignature.split(',').length,
        });
    }, [unresolvableSignature]);

    /**
     * Close a row's task once coverage says its account owes nothing.
     *
     * Keyed on `rows`, not on the upload callback, because "all statements uploaded" is not a fact
     * about the upload: coverage credits a month only once extraction has *filed* the statement, so
     * at the moment an upload saves the row is still outstanding. Every path that can change the
     * answer refetches coverage, so watching the derived rows catches all of them.
     *
     * Three guards, because a wrong completion is permanent (`toRows`): coverage has to be usable,
     * so a failed load cannot read as "nothing missing"; the state has to be `complete` rather than
     * `unknown`; and the task has to still be active, since the panel fetches completed ones too.
     */
    const autoCompletedRef = useRef(new Set<string>());
    const { mutateAsync: updateTaskStatus } = useUpdateOperatorTaskStatus();
    const invalidateMemberTaskQueries = useInvalidateMemberTaskQueries();

    useEffect(() => {
        if (!open || !isCoverageUsable(coverage)) {
            return;
        }
        const settled = rows.filter(
            (row) =>
                row.state === 'complete' &&
                (ACTIVE_TASK_STATUSES as readonly TaskStatus[]).includes(row.task.status) &&
                // The refetch this triggers is not instant, so the same row arrives `ready` again
                // on the next render or two. Without this each of those fires another completion.
                !autoCompletedRef.current.has(row.task.id)
        );
        if (settled.length === 0) {
            return;
        }
        // Claimed before any request goes out, so a re-render while they are in flight cannot
        // fire a second completion for the same row. A failure below puts its id back.
        settled.forEach((row) => autoCompletedRef.current.add(row.task.id));
        // `mutateAsync` per row rather than `mutate` with callbacks, because these N calls share
        // one mutation observer: it holds a single set of `mutateOptions` and detaches itself
        // from the previous mutation on every call, so with two rows settling in the same commit
        // only the last row's `onError` would ever run. The requests all still go out — it is the
        // failure handling that is lost, which is the half that has to be per-row.
        Promise.all(
            settled.map(async (row) => {
                try {
                    await updateTaskStatus({ id: row.task.id, status: OperatorTaskStatus.COMPLETED });
                    return true;
                } catch (error) {
                    // Back out of the ref so the next refetch retries. Silent to the member:
                    // they have supplied everything, and a failure here costs them nothing they
                    // can see or act on — the row already reads as complete.
                    autoCompletedRef.current.delete(row.task.id);
                    sendDatadogRumError('Auto-completing a back-books task failed', error, {
                        source: 'BuildingBackBooksGroup',
                        taskId: row.task.id,
                    });
                    return false;
                }
            })
        ).then((completions) => {
            // Once for the batch, and only if something landed. Passing the group id is what
            // keeps the card from being pulled out from under the open panel: the helper then
            // refreshes only the task list, and leaves the group summary to refresh when the
            // drawer closes.
            if (completions.some(Boolean)) {
                invalidateMemberTaskQueries({ group_id: group.id });
            }
        });
    }, [open, rows, coverage, group.id, updateTaskStatus, invalidateMemberTaskQueries]);
    const activeRow = rows.find((row) => row.task.id === activeRowKey) ?? null;
    // Read on its own rather than through `activeRow`, so the write callbacks below keep a stable
    // identity across coverage refetches — one of them is passed into a mounted modal.
    const activeBankAccountId = activeRow?.account.bank_account_id ?? null;
    const activeAccountMask = activeRow?.account.account_mask ?? '';
    const activeHeldPeriods = activeRow?.account.held_periods;

    const documentsByUuid = useMemo(
        () => new Map((documents ?? []).map((document: Document) => [document.uuid, document])),
        [documents]
    );

    /**
     * Every document uploaded against this account, whatever became of it.
     *
     * Read from the statuses map rather than coverage's `held_periods`, which lists only months that
     * were credited: a file that failed, landed on the wrong account or could not be read has no
     * held period, so deriving from coverage showed the member an empty slot and no reason for it.
     * The statuses map carries an entry per document, so the modal can state what happened to each.
     *
     * Still intersected with the live document list, which is where the file name comes from — and
     * which also means a file deleted elsewhere disappears rather than lingering as a row nothing
     * will ever resolve.
     */
    const activeFiles = useMemo<StatementFile[]>(() => {
        if (!activeRow) {
            return [];
        }
        const belongsHere = (documentUuid: string) => {
            // Either fact is sufficient, and the session record is checked regardless of whether a
            // status exists. Checking it only when the status is absent was the bug: the endpoint
            // answers for every uploaded document, so a document whose filing failed arrives with a
            // null account, fails the join, and would drop off the row the moment the server spoke.
            if (uploadsThisSessionRef.current.get(documentUuid)?.rowKey === activeRow.task.id) {
                return true;
            }
            const status = statuses?.[documentUuid];
            return !!status && statusBelongsToAccount(status, activeRow.account);
        };

        return [...documentsByUuid.values()].flatMap((document: Document) => {
            if (!belongsHere(document.uuid)) {
                return [];
            }
            if (!isRelevantToAsk(statuses?.[document.uuid], document.created_at, activeRow)) {
                return [];
            }
            return [{ documentUuid: document.uuid, fileName: document.file_name, uploadedAt: document.created_at }];
        });
    }, [activeRow, documentsByUuid, statuses]);

    /**
     * Which months each held document was credited for, read straight off coverage.
     *
     * The server owns this: `reassign-periods` moves the tracker row and `held_periods` is that row
     * read back. Deriving it rather than storing a parallel copy is what stops the two disagreeing.
     *
     * Grouped rather than keyed one-per-document. A single PDF can carry several monthly statements
     * and holds a row for each, so `held_periods` legitimately lists a document more than once —
     * flattened into one entry per document, every month but the last silently disappeared.
     */
    const heldPeriodsByUuid = useMemo(
        () => groupHeldPeriods(activeRow?.account.held_periods),
        [activeRow?.account.held_periods]
    );

    const handleUploadClick = useCallback(
        (rowKey: string) => {
            const row = rows.find((candidate) => candidate.task.id === rowKey);
            // An account with no v6 id cannot have anything filed against it, so opening the modal
            // would offer an upload that silently goes nowhere. Say so instead.
            if (row && !canFileUploads(row.account)) {
                enqueueSnackbar(ACCOUNT_NOT_READY_MESSAGE, {
                    variant: 'warning',
                    autoHideDuration: ERROR_SNACKBAR_DURATION_MS,
                });
                return;
            }
            setActiveRowKey(rowKey);
        },
        [rows, enqueueSnackbar]
    );

    const handleModalClose = useCallback(() => setActiveRowKey(null), []);

    /**
     * Clear the open row before closing. This component stays mounted with `open={false}`, so a key
     * left set would re-surface the modal the next time the panel opens.
     */
    const handleDrawerClose = useCallback(() => {
        setActiveRowKey(null);
        onClose();
    }, [onClose]);

    // Not coverage: a month is credited only once extraction files the statement, minutes later, so
    // refreshing it here spends a Tight-plus-Plaid round trip on a guaranteed-unchanged answer. The
    // modal refreshes it when the verdicts settle. The task helper owns the grouped-vs-ungrouped
    // branch, and the statuses map is what makes a new upload appear on a row at all.
    const invalidateAfterUpload = useCallback(() => {
        queryClient.invalidateQueries({ queryKey: ['documents'] });
        invalidateMemberTaskQueries({ group_id: group.id });
        queryClient.invalidateQueries({ queryKey: STATEMENT_UPLOAD_STATUSES_QUERY_KEY });
    }, [queryClient, invalidateMemberTaskQueries, group.id]);

    /** A month correction moves held months server-side, so coverage is stale now. */
    const invalidateAfterCorrection = useCallback(() => {
        queryClient.invalidateQueries({ queryKey: STATEMENT_COVERAGE_QUERY_KEY });
        invalidateAfterUpload();
    }, [queryClient, invalidateAfterUpload]);

    /**
     * Files each newly uploaded document against the account it was dropped on.
     *
     * The dropzone reports its whole accumulated session per batch, so this dedupes before sending —
     * each call is a live round trip. An account Tight has not assigned an id to is skipped, because
     * the endpoint keys on that id.
     */
    const handleUploadSuccess = useCallback(
        async (uploaded?: Document[]) => {
            if (memberId == null || !activeBankAccountId || !activeRowKey) {
                invalidateAfterUpload();
                return;
            }

            const fresh = (uploaded ?? []).filter(
                (document) => !uploadsThisSessionRef.current.get(document.uuid)?.isFiled
            );
            // Recorded before the attempt so each row shows its document either way; `isFiled` flips
            // only on success, which is what keeps a failure inside `fresh` on the next report.
            fresh.forEach((document) =>
                uploadsThisSessionRef.current.set(document.uuid, { rowKey: activeRowKey, isFiled: false })
            );

            const { recorded, failed } = await fileDocuments(
                fresh.map((document) => document.uuid),
                activeBankAccountId
            );
            recorded.forEach((uuid) =>
                uploadsThisSessionRef.current.set(uuid, { rowKey: activeRowKey, isFiled: true })
            );

            if (failed.length > 0) {
                // Announced here where onboarding stays silent: there the file is still processed
                // asynchronously, but on this surface the filing is what puts it on a row at all.
                enqueueSnackbar(UPLOAD_NOT_FILED_MESSAGE, {
                    variant: 'error',
                    autoHideDuration: ERROR_SNACKBAR_DURATION_MS,
                });
            }
            invalidateAfterUpload();
        },
        [activeBankAccountId, activeRowKey, memberId, fileDocuments, invalidateAfterUpload, enqueueSnackbar]
    );

    const handleRemove = useCallback(
        async (documentUuid: string, period?: StatementPeriod) => {
            const account = { bank_account_id: activeBankAccountId, account_mask: activeAccountMask };
            // The shared write reports its own failure and refetches coverage, the statuses and the
            // documents before resolving; this surface only has to refresh the task list.
            if (!(await removeFromAccount(documentUuid, account, period))) {
                return;
            }
            // The session record pins a file to the row it was dropped on whatever the server says,
            // and the server re-homes a file once its card holds none of it. Left pinned, it would
            // stay on this row reading another card's months as its own.
            const heldAccount = { bank_account_id: activeBankAccountId, held_periods: activeHeldPeriods };
            if (!holdsMonthAfterRemoval(documentUuid, heldAccount, period)) {
                uploadsThisSessionRef.current.delete(documentUuid);
            }
            invalidateMemberTaskQueries({ group_id: group.id });
        },
        [
            activeBankAccountId,
            activeAccountMask,
            activeHeldPeriods,
            removeFromAccount,
            holdsMonthAfterRemoval,
            invalidateMemberTaskQueries,
            group.id,
        ]
    );

    const handleSavePeriods = useCallback(
        async (assignments: StatementPeriodAssignment[]) => {
            // Returning false keeps the modal open with the member's edits intact; the shared write
            // has already said why. Coverage and the verdicts are refreshed there, so the extra
            // invalidation here is only this surface's own — documents and the task list.
            if (!(await savePeriods(assignments, activeBankAccountId))) {
                return false;
            }
            invalidateAfterCorrection();
            return true;
        },
        [savePeriods, activeBankAccountId, invalidateAfterCorrection]
    );

    const isLoading = isCoveragePending || isTasksLoading;
    const isError = isTasksError || (isCoverageError && !coverage);
    // The two queries the modal's file list is derived from. Prefetching above makes a pending
    // file list nearly unreachable — a click would have to beat the request — but "nearly" is not
    // "never" on a slow connection, and the failure it leaves is the misleading empty list.
    const isFileListError = isStatusesError || isDocumentsError;
    const isFileListPending = isStatusesPending || isDocumentsPending;

    return (
        <TaskGroupDrawer group={group} open={open} onClose={handleDrawerClose}>
            <GroupBody
                rows={rows}
                isLoading={isLoading}
                isError={isError}
                isCoverageUnusable={!isLoading && !isError && !isCoverageUsable(coverage)}
                onUploadClick={handleUploadClick}
            />

            {/* An errored file list is indistinguishable from an empty one, and the member's response
                to "you have uploaded nothing" is to upload it all again. Refuse rather than mislead. */}
            {activeRow && isFileListError && (
                <Alert severity="error">
                    We couldn&apos;t load the files for this account. Please close and try again.
                </Alert>
            )}

            {/* Not an empty modal: opening on a list that has not arrived says "you have uploaded
                nothing", which is the one thing it must never say wrongly. */}
            {activeRow && !isFileListError && isFileListPending && (
                <Stack alignItems="center" sx={{ py: 2 }}>
                    <CircularProgress size={24} />
                </Stack>
            )}

            {/* Mounted only while open: the modal stages month corrections and relies on unmounting
                to discard them, so Cancel and close have to be the same action. */}
            {activeRow && !isFileListError && !isFileListPending && (
                <VerifiedStatementUploadModal
                    open
                    onClose={handleModalClose}
                    account={activeRow.account}
                    // The task's ask, not the account's window: the modal lists a row per month it
                    // is given, and the window would put months this task never asked about on
                    // screen with a delete beside every statement already credited to them.
                    requiredPeriods={activeRow.askedPeriods}
                    files={activeFiles}
                    documentsByUuid={documentsByUuid}
                    onRemove={handleRemove}
                    heldPeriodsByUuid={heldPeriodsByUuid}
                    onSavePeriods={handleSavePeriods}
                    onUploadSuccess={handleUploadSuccess}
                />
            )}
        </TaskGroupDrawer>
    );
}
